import React, { useState, useEffect } from 'react';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { assignmentService } from '../../../services/assignmentService';
import { toast } from 'react-toastify';
import {
  User,
  Shield,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Wrench,
  Package,
  UserCheck
} from 'lucide-react';

interface ResourceItem {
  _id: string;
  name: string;
  email?: string;
  role: { _id: string; name: string };
  totalEquipmentAssigned?: number;
}

interface EquipmentAssignmentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  selectedEquipmentIds: string[];
  equipmentList: unknown[];
  onAssignmentComplete: () => void;
}

export default function EquipmentAssignmentDrawer({
  isOpen,
  onClose,
  projectId,
  selectedEquipmentIds,
  equipmentList,
  onAssignmentComplete
}: EquipmentAssignmentDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [availableResources, setAvailableResources] = useState<ResourceItem[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<string>('');
  const [selectedResourceRole, setSelectedResourceRole] = useState<'Site Engineer' | 'Site Supervisor'>('Site Engineer');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>('');
  const [assigning, setAssigning] = useState(false);
  const [supervisorsExpanded, setSupervisorsExpanded] = useState(true);
  const [engineersExpanded, setEngineersExpanded] = useState(true);
  const [equipmentListExpanded, setEquipmentListExpanded] = useState(false);

  useEffect(() => {
    if (isOpen && projectId) {
      loadResources();
    }
  }, [isOpen, projectId]);

  const loadResources = async () => {
    setLoading(true);
    try {
      const response = await assignmentService.getEngineersWithAssignments({ projectId });
      setAvailableResources(response?.engineers || []);
      setSelectedResourceId('');
      setSelectedSupervisorId('');
    } catch (error) {
      console.error('Failed to load resources:', error);
      toast.error('Failed to load available resources');
    } finally {
      setLoading(false);
    }
  };

  const selectedEquipment = equipmentList.filter(
    (eq: unknown) =>
      selectedEquipmentIds.includes(
        (eq as { _id?: string; id?: string })?._id || (eq as { id?: string })?.id || ''
      )
  ) as Array<{
    _id?: string;
    id?: string;
    equipmentName?: string;
    name?: string;
    assetType?: string;
    siteEngineerId?: string;
    siteSupervisorId?: string;
  }>;

  const supervisors = availableResources.filter((r) => r.role?.name === 'Site Supervisor');
  const engineers = availableResources.filter((r) => r.role?.name === 'Site Engineer');
  const hasReassign = selectedEquipment.some((eq) => eq.siteEngineerId || eq.siteSupervisorId);
  const reassignCount = selectedEquipment.filter((eq) => eq.siteEngineerId || eq.siteSupervisorId).length;

  // Auto-select resource when only one option exists in a category
  useEffect(() => {
    if (!selectedResourceId && !loading) {
      if (engineers.length === 1 && supervisors.length === 0) {
        handleSelectResource(engineers[0]._id, 'Site Engineer');
      } else if (supervisors.length === 1 && engineers.length === 0) {
        handleSelectResource(supervisors[0]._id, 'Site Supervisor');
      }
    }
  }, [availableResources, loading]);

  // Auto-select supervisor dropdown when only one supervisor exists
  useEffect(() => {
    if (selectedResourceRole === 'Site Engineer' && selectedResourceId && supervisors.length === 1) {
      setSelectedSupervisorId(supervisors[0]._id);
    }
  }, [selectedResourceRole, selectedResourceId, supervisors.length]);

  const handleSelectResource = (resourceId: string, role: 'Site Engineer' | 'Site Supervisor') => {
    setSelectedResourceId(resourceId);
    setSelectedResourceRole(role);
    if (role === 'Site Engineer') setSelectedSupervisorId('');
  };

  const handleAssign = async () => {
    if (!selectedResourceId) {
      toast.error('Select a resource from the tree');
      return;
    }
    setAssigning(true);
    try {
      const assignmentData: {
        projectId: string;
        assignmentType: 'equipment';
        targetIds: string[];
        assigneeId: string;
        assigneeRole: 'Site Engineer' | 'Site Supervisor';
        supervisorId?: string;
        expandDescendants: boolean;
      } = {
        projectId,
        assignmentType: 'equipment',
        targetIds: selectedEquipmentIds,
        assigneeId: selectedResourceId,
        assigneeRole: selectedResourceRole,
        expandDescendants: true
      };
      if (selectedResourceRole === 'Site Engineer' && selectedSupervisorId) {
        assignmentData.supervisorId = selectedSupervisorId;
      }
      const apiResult = await assignmentService.createBulkAssignment(assignmentData);
      const assignedCount =
        typeof apiResult?.data?.equipmentAssigned === 'number'
          ? apiResult.data.equipmentAssigned
          : selectedEquipmentIds.length;
      toast.success(
        assignedCount === selectedEquipmentIds.length
          ? `Assigned ${assignedCount} equipment`
          : `Assigned ${assignedCount} equipment (all descendants under ${selectedEquipmentIds.length} selected)`
      );
      onAssignmentComplete();
      onClose();
    } catch (error: unknown) {
      const err = error as { message?: string };
      console.error('Assignment failed:', error);
      toast.error(err?.message || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const isSelected = (id: string) => selectedResourceId === id;
  const eqCount = (r: ResourceItem) =>
    (r as ResourceItem & { totalEquipmentAssigned?: number }).totalEquipmentAssigned ?? 0;

  const selectedName =
    selectedResourceId &&
    (supervisors.find((s) => s._id === selectedResourceId) || engineers.find((e) => e._id === selectedResourceId))
      ?.name;



  return (
    <RightDrawer open={isOpen} onClose={onClose} title="Assign to resource" size="md">
      <div className="flex flex-col h-full min-h-0">
        {/* Compact summary bar */}
        <div className="flex-shrink-0 flex items-center gap-2 px-1 py-2 border-b border-slate-200 bg-slate-50/80">
          <Package size={16} className="text-slate-500" />
          <span className="text-sm font-medium text-slate-700">
            <strong className="text-slate-900 tabular-nums">{selectedEquipmentIds.length}</strong> selected
            <span className="text-slate-500 font-normal"> (descendants included on assign)</span>
          </span>
          {selectedName && (
            <>
              <span className="text-slate-400">→</span>
              <span className="text-sm text-slate-700 truncate max-w-[140px]" title={selectedName}>
                {selectedName}
              </span>
            </>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Resource tree - compact */}
          <div className="p-3">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Resource tree</p>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-500" size={24} />
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                {/* Site Supervisors */}
                <div className="border-b border-slate-100 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setSupervisorsExpanded(!supervisorsExpanded)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50/90 hover:bg-slate-100 text-left transition-colors"
                  >
                    {supervisorsExpanded ? (
                      <ChevronDown size={14} className="text-slate-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={14} className="text-slate-500 flex-shrink-0" />
                    )}
                    <Shield size={14} className="text-violet-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-800">Site Supervisors</span>
                    <span className="ml-auto text-[10px] font-medium text-slate-500 bg-slate-200/90 px-1.5 py-px rounded">
                      {supervisors.length}
                    </span>
                  </button>
                  {supervisorsExpanded && (
                    <div className="bg-white">
                      {supervisors.length === 0 ? (
                        <div className="px-3 py-2 pl-8 text-xs text-slate-500">None assigned</div>
                      ) : (
                        <ul>
                          {supervisors.map((r) => (
                            <li key={r._id}>
                              <button
                                type="button"
                                onClick={() => handleSelectResource(r._id, 'Site Supervisor')}
                                title={r.name}
                                className={`w-full flex items-center gap-2 pl-8 pr-3 py-2 text-left border-l-2 transition-colors ${
                                  isSelected(r._id)
                                    ? 'bg-violet-50 border-l-violet-600'
                                    : 'border-l-transparent hover:bg-slate-50'
                                }`}
                              >
                                <User size={12} className="text-slate-400 flex-shrink-0" />
                                <span className="text-xs font-medium text-slate-800 truncate min-w-0 flex-1">
                                  {r.name}
                                </span>
                                <span className="text-[10px] text-slate-500 tabular-nums flex-shrink-0">
                                  {eqCount(r)} eq
                                </span>
                                {isSelected(r._id) && (
                                  <CheckCircle size={12} className="text-violet-600 flex-shrink-0" />
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Site Engineers */}
                <div>
                  <button
                    type="button"
                    onClick={() => setEngineersExpanded(!engineersExpanded)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50/90 hover:bg-slate-100 text-left transition-colors"
                  >
                    {engineersExpanded ? (
                      <ChevronDown size={14} className="text-slate-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={14} className="text-slate-500 flex-shrink-0" />
                    )}
                    <Wrench size={14} className="text-blue-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-800">Site Engineers</span>
                    <span className="ml-auto text-[10px] font-medium text-slate-500 bg-slate-200/90 px-1.5 py-px rounded">
                      {engineers.length}
                    </span>
                  </button>
                  {engineersExpanded && (
                    <div className="bg-white">
                      {engineers.length === 0 ? (
                        <div className="px-3 py-2 pl-8 text-xs text-slate-500">None assigned</div>
                      ) : (
                        <ul>
                          {engineers.map((r) => (
                            <li key={r._id}>
                              <button
                                type="button"
                                onClick={() => handleSelectResource(r._id, 'Site Engineer')}
                                title={r.name}
                                className={`w-full flex items-center gap-2 pl-8 pr-3 py-2 text-left border-l-2 transition-colors ${
                                  isSelected(r._id)
                                    ? 'bg-blue-50 border-l-blue-600'
                                    : 'border-l-transparent hover:bg-slate-50'
                                }`}
                              >
                                <User size={12} className="text-slate-400 flex-shrink-0" />
                                <span className="text-xs font-medium text-slate-800 truncate min-w-0 flex-1">
                                  {r.name}
                                </span>
                                <span className="text-[10px] text-slate-500 tabular-nums flex-shrink-0">
                                  {eqCount(r)} eq
                                </span>
                                {isSelected(r._id) && (
                                  <CheckCircle size={12} className="text-blue-600 flex-shrink-0" />
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Under Supervisor - compact, only when engineer selected */}
            {selectedResourceRole === 'Site Engineer' && selectedResourceId && supervisors.length > 0 && (
              <div className="mt-3">
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1">
                  Under supervisor (optional)
                </label>
                <select
                  value={selectedSupervisorId}
                  onChange={(e) => setSelectedSupervisorId(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">— None —</option>
                  {supervisors.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Collapsible equipment list */}
            <div className="mt-3 border border-slate-200 rounded-lg bg-slate-50/50 overflow-hidden">
              <button
                type="button"
                onClick={() => setEquipmentListExpanded(!equipmentListExpanded)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100/80 transition-colors"
              >
                {equipmentListExpanded ? (
                  <ChevronDown size={12} className="text-slate-500" />
                ) : (
                  <ChevronRight size={12} className="text-slate-500" />
                )}
                <span className="text-xs font-medium text-slate-700">
                  Selected equipment ({selectedEquipment.length})
                </span>
              </button>
              {equipmentListExpanded && (
                <div className="max-h-32 overflow-y-auto border-t border-slate-200 bg-white px-3 py-2">
                  <ul className="space-y-1">
                    {selectedEquipment.map((eq, idx) => (
                      <li
                        key={eq._id || eq.id || idx}
                        className="flex items-center gap-2 text-xs text-slate-700"
                        title={eq.assetType ? `${eq.equipmentName || eq.name} (${eq.assetType})` : undefined}
                      >
                        <span className="text-slate-400 font-mono w-5">{idx + 1}.</span>
                        <span className="truncate min-w-0 flex-1">{eq.equipmentName || eq.name || 'Equipment'}</span>
                        {eq.assetType && (
                          <span className="text-slate-500 flex-shrink-0 text-[10px]">({eq.assetType})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Compact warning */}
            {hasReassign && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
                <AlertCircle size={14} className="text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  <strong>{reassignCount}</strong> item(s) will be reassigned.
                </p>
              </div>
            )}


          </div>
        </div>

        {/* Footer - compact */}
        <div className="flex-shrink-0 flex gap-2 p-3 border-t border-slate-200 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={assigning}
            className="flex-1 px-3 py-2.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={!selectedResourceId || assigning}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {assigning ? (
              <>
                <Loader2 className="animate-spin" size={14} />
                Assigning…
              </>
            ) : (
              <>
                <UserCheck size={14} />
                Assign
              </>
            )}
          </button>
        </div>
      </div>
    </RightDrawer>
  );
}
